import React from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { DialogProps } from '@mui/material/Dialog';
import { PaperProps } from '@mui/material/Paper';

/**
 * Base dialog modes
 */
export type BaseDialogMode = 'modal' | 'embedded';

/**
 * Dialog variants for different use cases
 */
export type DialogVariant = 'default' | 'fullscreen' | 'compact';

/**
 * Dialog close reasons
 */
export type DialogCloseReason = 'backdropClick' | 'escapeKeyDown';

/**
 * Custom slots for BaseDialog
 */
export interface BaseDialogSlots {
  header?: React.ComponentType<any>;
  footer?: React.ComponentType<any>;
  paper?: React.ComponentType<PaperProps>;
}

/**
 * Props for custom slots
 */
export interface BaseDialogSlotProps {
  header?: Record<string, any>;
  footer?: Record<string, any>;
  paper?: PaperProps;
  backdrop?: {
    sx?: SxProps<Theme>;
    [key: string]: any;
  };
}

/**
 * Generic BaseDialog props interface
 * TData - type for any data passed to dialog content
 */
export interface BaseDialogProps<TData = any> {
  /**
   * Whether the dialog is open - CONTROLLED BY PARENT
   */
  open: boolean;

  /**
   * Callback fired when the dialog should close
   * Reason indicates how the close was triggered
   */
  onClose?: (event: {}, reason: DialogCloseReason) => void;

  /**
   * Dialog mode: modal (with backdrop) or embedded (inline component)
   * @default 'modal'
   */
  mode?: BaseDialogMode;

  /**
   * Dialog title (if using default header)
   */
  title?: string;

  /**
   * Dialog header content
   */
  header?: React.ReactNode;

  /**
   * Dialog content
   */
  children: React.ReactNode;

  /**
   * Dialog footer content
   */
  footer?: React.ReactNode;

  /**
   * Custom slots for different parts of the dialog
   */
  slots?: BaseDialogSlots;

  /**
   * Props passed to slot components
   */
  slotProps?: BaseDialogSlotProps;

  /**
   * Dialog variant for different styling approaches
   * @default 'default'
   */
  variant?: DialogVariant;

  /**
   * Whether dialog should be full screen
   * Useful for mobile responsiveness
   */
  fullScreen?: boolean;

  /**
   * Whether dialog should fill the available width
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Maximum width of the dialog
   * @default 'sm'
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;

  /**
   * Disable the escape key behavior
   * @default false
   */
  disableEscapeKeyDown?: boolean;

  /**
   * If true, clicking outside the dialog will not close it
   * @default false
   */
  disableBackdropClick?: boolean;

  /**
   * Keep the dialog mounted in the DOM when closed
   * Useful for performance or SEO
   * @default false
   */
  keepMounted?: boolean;

  /**
   * Disable auto focus when dialog opens
   * @default false
   */
  disableAutoFocus?: boolean;

  /**
   * Disable restoring focus when dialog closes
   * @default false
   */
  disableRestoreFocus?: boolean;

  /**
   * Disable focus management inside dialog
   * @default false
   */
  disableEnforceFocus?: boolean;

  /**
   * Custom styles for the dialog container
   */
  sx?: SxProps<Theme>;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Custom data passed to dialog content
   */
  data?: TData;

  /**
   * Callback fired when data changes
   */
  onDataChange?: (data: TData) => void;

  /**
   * Z-index for dialog and backdrop
   */
  zIndex?: number;

  /**
   * Transition component for animations
   */
  TransitionComponent?: React.ComponentType<any>;

  /**
   * Duration for enter/exit transitions
   */
  transitionDuration?: number | { enter?: number; exit?: number };

  /**
   * Props passed to transition component
   */
  TransitionProps?: any;

  /**
   * Callback fired when dialog opens
   */
  onOpen?: () => void;

  /**
   * Callback fired when transition ends
   */
  onTransitionEnd?: () => void;

  /**
   * Callback fired when exit transition completes
   */
  onTransitionExited?: () => void;

  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;

  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;

  /**
   * ARIA labelledby for accessibility
   */
  'aria-labelledby'?: string;

  /**
   * ARIA describedby for accessibility
   */
  'aria-describedby'?: string;
}

/**
 * Computed styles interface for theme integration
 */
export interface BaseDialogStyles {
  paper: SxProps<Theme>;
  backdrop: SxProps<Theme>;
  container: SxProps<Theme>;
}

/**
 * Dialog state management interface
 */
export interface BaseDialogState<TData = any> {
  /**
   * Whether dialog is currently open
   */
  isOpen: boolean;

  /**
   * Loading state for async operations
   */
  isLoading: boolean;

  /**
   * Error message if any
   */
  error: string | null;

  /**
   * Current data state
   */
  data: TData | null;
}

/**
 * Dialog actions interface
 */
export interface BaseDialogActions<TData = any> {
  /**
   * Open the dialog
   */
  open: () => void;

  /**
   * Close the dialog
   */
  close: () => void;

  /**
   * Toggle dialog open/closed state
   */
  toggle: () => void;

  /**
   * Set dialog data
   */
  setData: (data: TData) => void;

  /**
   * Clear dialog data
   */
  clearData: () => void;

  /**
   * Set loading state
   */
  setLoading: (loading: boolean) => void;

  /**
   * Set error state
   */
  setError: (error: string | null) => void;
}

/**
 * Dialog computed properties interface
 */
export interface BaseDialogComputed {
  /**
   * Whether dialog can be closed (based on validation/changes)
   */
  canClose: boolean;

  /**
   * Whether dialog has unsaved changes
   */
  hasChanges: boolean;

  /**
   * Whether dialog data is valid
   */
  isValid: boolean;

  /**
   * List of changed fields (from advanced change detection)
   */
  changes?: Array<{
    path: string;
    oldValue: any;
    newValue: any;
    changeType: 'added' | 'removed' | 'modified';
  }>;

  /**
   * Performance statistics for change detection
   */
  stats?: {
    comparisons: number;
    lastComparisonTime: number;
    averageComparisonTime: number;
    totalComparisonTime: number;
    objectSize: number;
  };
}

/**
 * Controller hook return type
 */
export interface BaseDialogController<TData = any> {
  /**
   * Computed styles using theme tokens
   */
  styles: BaseDialogStyles;

  /**
   * Enhanced onClose handler with reason filtering
   */
  handleClose: (event: {}, reason: DialogCloseReason) => void;

  /**
   * Props prepared for MUI Dialog component
   */
  dialogProps: Partial<DialogProps>;

  /**
   * Props prepared for Paper component (embedded mode)
   */
  paperProps: PaperProps;

  /**
   * Whether component should render as modal
   */
  isModal: boolean;

  /**
   * Whether component should render as embedded
   */
  isEmbedded: boolean;

  /**
   * Dialog state management
   */
  state: BaseDialogState<TData>;

  /**
   * Dialog actions
   */
  actions: BaseDialogActions<TData>;

  /**
   * Computed properties
   */
  computed: BaseDialogComputed;
}

/**
 * Theme-aware dialog configuration
 */
export interface BaseDialogConfig {
  /**
   * Default mode for new dialogs
   */
  defaultMode: BaseDialogMode;

  /**
   * Default variant for new dialogs
   */
  defaultVariant: DialogVariant;

  /**
   * Default max width
   */
  defaultMaxWidth: BaseDialogProps['maxWidth'];

  /**
   * Default backdrop behavior
   */
  defaultDisableBackdropClick: boolean;

  /**
   * Default escape key behavior
   */
  defaultDisableEscapeKeyDown: boolean;

  /**
   * Default auto focus behavior
   */
  defaultDisableAutoFocus: boolean;

  /**
   * Default restore focus behavior
   */
  defaultDisableRestoreFocus: boolean;

  /**
   * Default enforce focus behavior
   */
  defaultDisableEnforceFocus: boolean;

  /**
   * Default keep mounted behavior
   */
  defaultKeepMounted: boolean;

  /**
   * Default z-index for dialogs
   */
  defaultZIndex: number;
}

/**
 * Default configuration values
 */
export const DEFAULT_BASE_DIALOG_CONFIG: BaseDialogConfig = {
  defaultMode: 'modal',
  defaultVariant: 'default',
  defaultMaxWidth: 'sm',
  defaultDisableBackdropClick: false,
  defaultDisableEscapeKeyDown: false,
  defaultDisableAutoFocus: false,
  defaultDisableRestoreFocus: false,
  defaultDisableEnforceFocus: false,
  defaultKeepMounted: false,
  defaultZIndex: 1300, // Material-UI default modal z-index
};

/**
 * Type guard to check if mode is modal
 */
export const isModalMode = (mode?: BaseDialogMode): boolean => {
  return mode === 'modal' || mode === undefined;
};

/**
 * Type guard to check if mode is embedded
 */
export const isEmbeddedMode = (mode?: BaseDialogMode): boolean => {
  return mode === 'embedded';
};

/**
 * Type guard to check if variant is fullscreen
 */
export const isFullscreenVariant = (variant?: DialogVariant): boolean => {
  return variant === 'fullscreen';
};

/**
 * Type guard to check if variant is compact
 */
export const isCompactVariant = (variant?: DialogVariant): boolean => {
  return variant === 'compact';
};

/**
 * Type guard to check if variant is default
 */
export const isDefaultVariant = (variant?: DialogVariant): boolean => {
  return variant === 'default' || variant === undefined;
};